import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { makeAutoObservable } from 'mobx';
import {
    CenteredVerifyHeadline,
    VerifyInfo,
    VerifyButton,
    VerifyInfoExtra,
} from 'src/domains/players/webview/components/SignUp/signupPrimary/signUpViews/profileVerification/ProfileVerification.style';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import {
    VerificationFailedWrapper,
    WarningIconWrapper,
    WarningIconContainer,
    EmailLinkNew,
    CloseWrapper,
    CloseTitle,
    HeaderCloseIcon,
} from './VerificationFailed.style';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { SignUpState } from 'src/domains/players/webview/components/SignUp';
import { ModalState } from 'src/domains/layouts/webview/components/modals/Modal.state';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { IdentityState } from 'src/domains/players/state/IdMission/IdentityState';
import { AppPlayersState } from 'src/domains/players/state/AppPlayers.state';

export class VerificationFailedPageState {
    public isVerifyButtonDisabled = false;

    public constructor(
        public readonly signUpState: SignUpState,
        public readonly router: StarRouter,
        public readonly modalState: ModalState,
        private readonly identityState: IdentityState,
        private readonly accountId: number | null
    ) {
        makeAutoObservable(this);
    }

    public onHomeClick = async (): Promise<void> => {
        this.router.redirectToHomepage();
        await this.modalState.closeAll();
    };

    public onVerifyClick = async (): Promise<void> => {
        this.isVerifyButtonDisabled = true;

        if (this.accountId === null) {
            return;
        }

        this.identityState.setUnauthorizedUserId(this.accountId);
        await this.identityState.runManualTrigger(this.accountId);
        this.identityState.refreshIdMissionData();
        await this.modalState.closeAll();
    };
}

interface PropsType {
    signUpState: SignUpState;
    accountId: number | null;
}

export const VerificationFailedPage = observer('VerificationFailedPage', ({ signUpState, accountId }: PropsType) => {
    const common = useCommon();
    const appPlayersState = AppPlayersState.get(common);
    const { modalState, identityState } = appPlayersState;
    const config = ConfigComponents.get(common).config;
    const router = StarRouter.get(common);
    const [state] = useState(
        () => new VerificationFailedPageState(signUpState, router, modalState, identityState, accountId)
    );
    const { signUpFooterMail, idMission } = config;

    return (
        <VerificationFailedWrapper>
            <CloseWrapper>
                <CloseTitle>
                    <I18n
                        langKey='signup-journey.verification-failed.header'
                        defaultText='Sign Up'
                    />
                </CloseTitle>
                <HeaderCloseIcon
                    data-test='home-button'
                    onClick={state.onHomeClick}
                />
            </CloseWrapper>
            <WarningIconContainer>
                <WarningIconWrapper />
            </WarningIconContainer>
            <CenteredVerifyHeadline data-test='centered-verify-headline'>
                <I18n
                    langKey='signup-journey.verification-failed.headline'
                    defaultText='Account Suspended'
                />
            </CenteredVerifyHeadline>
            <VerifyInfo data-test='verify-info'>
                {idMission ? (
                    <I18n
                        langKey='signup-journey.verification-failed.info1-verification'
                        defaultText="Your account has been temporarily suspended due to a verification issue. Please click 'Verify' below to re-initiate the verification process."
                    />
                ) : (
                    <I18n
                        langKey='signup-journey.verification-failed.info1'
                        defaultText='Your account has been suspended pending verification.'
                    />
                )}
            </VerifyInfo>
            <VerifyInfoExtra data-test='verify-info'>
                <I18n
                    langKey='signup-journey.verification-failed.info2'
                    defaultText='If you continue to experience issues, please contact our support team at'
                />
            </VerifyInfoExtra>
            <EmailLinkNew href={`mailto: ${signUpFooterMail}`}> {signUpFooterMail}</EmailLinkNew>

            <VerifyInfoExtra data-test='verify-info'>
                <I18n
                    langKey='signup-journey.verification-failed.info3'
                    // empty - this section is optional
                    defaultText=''
                />
            </VerifyInfoExtra>

            {idMission ? (
                <VerifyButton
                    size='large'
                    onClick={state.onVerifyClick}
                    disabled={state.isVerifyButtonDisabled}
                    dataTest='verify-button'
                >
                    <I18n
                        langKey='signup-journey.verification-failed.verify'
                        defaultText='Verify'
                    />
                </VerifyButton>
            ) : null}
        </VerificationFailedWrapper>
    );
});
